import React, { KeyboardEvent, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { CustomCell, CustomRenderer, GridCellKind, ProvideEditorComponent } from '@glideapps/glide-data-grid'
import removeMarkdown from 'remove-markdown'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { Button, ButtonVariant, ThemeMode } from '@makyoapp/makyo-ui'

import { RichTextCell } from '@/core/models/columns/richText'
import { MarkdownEditor } from '@/lib/markdown'

import { getEditorStyle } from '../helpers/editor-style-helper'

interface EditorProps {
  value: MarkdownCell
  onChange: (newValue: MarkdownCell) => void
  onFinishedEditing: (newValue: MarkdownCell | undefined) => MarkdownCell | undefined
}

const RichTextEditor = ({ value, onFinishedEditing }: EditorProps) => {
  const [currentValue, setCurrentValue] = useState<string>(value.data.cell.toString() || '')
  const containerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  const handleChange = useCallback((newMarkdown: string | undefined) => {
    const updatedValue = newMarkdown || ''
    setCurrentValue(updatedValue)
  }, [])

  const finishEditing = useCallback(() => {
    onFinishedEditing({
      ...value,
      data: {
        ...value.data,
        cell: value.data.cell.toCell(currentValue),
      },
      copyData: currentValue,
    })
  }, [currentValue, onFinishedEditing, value])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.stopPropagation()
    }
  }, [])

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown} className='h-full flex flex-col mk-bg'>
      <div className='min-h-[500px]'>
        <MarkdownEditor value={currentValue} onChange={handleChange} height='500px' />
      </div>

      <div className='flex-1 flex justify-between py-3 px-3 border-t bdr'>
        <Button onClick={() => onFinishedEditing(undefined)} variant={ButtonVariant.Outlined}>
          {t('Cancel')}
        </Button>
        <Button onClick={finishEditing}>{t('Save')}</Button>
      </div>
    </div>
  )
}

// Definition of Markdown cell properties structure
export interface MarkdownCellProps {
  readonly type: ColumnTypes
  readonly cell: RichTextCell
  readonly extra: {
    theme: ThemeMode
    dataEditorRef: React.RefObject<any>
  }
}

// Definition of MarkdownCell type
export type MarkdownCell = CustomCell<MarkdownCellProps>

// Icon properties interface
interface IconProperties {
  symbol: string
  xPosition: number
  width: number
  action: string
  color: string
  preventActivation: boolean
}

// Constants
const MAX_MARKDOWN_CHAR_TO_SHOW = 50
const ICON_FONT_STYLE = '20px Material Symbols Rounded'

/*
 * Function to truncate markdown display
 * It converts markdown to plain text and truncates if necessary
 */
const truncateMarkdownDisplay = (markdown: string): string => {
  const plainText = stripMarkdown(markdown)
  return plainText.length <= MAX_MARKDOWN_CHAR_TO_SHOW
    ? plainText
    : `${plainText.slice(0, MAX_MARKDOWN_CHAR_TO_SHOW)}...`
}

/*
 * Markdown Renderer
 * This custom renderer handles the display and interaction of Markdown cells
 */
const CustomRichTextRenderer: CustomRenderer<MarkdownCell> = {
  kind: GridCellKind.Custom,

  // Check if the cell is a Markdown cell
  isMatch: (cell: CustomCell): cell is MarkdownCell => (cell.data as MarkdownCellProps).type === ColumnTypes.RichText,

  // Measure the width of the cell content
  measure: (ctx, cell, theme) => {
    const markdownText = cell.data.cell.toString()
    const textToRender = truncateMarkdownDisplay(markdownText)

    return ctx.measureText(textToRender).width + theme.cellHorizontalPadding * 2 + 80
  },

  /*
   * Draw the cell content
   * This includes the truncated text and action icons on hover
   */
  draw: (args) => {
    const { cell, theme, rect, ctx, hoverAmount } = args
    const markdownText = cell.data.cell.toString()

    ctx.save()

    const textToRender = truncateMarkdownDisplay(markdownText)

    ctx.font = theme.baseFontFull
    ctx.fillStyle = theme.textDark

    const textMetrics = ctx.measureText(textToRender)
    const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent

    const verticalCenter = rect.y + rect.height / 2
    const textY = verticalCenter + textHeight / 2 - textMetrics.actualBoundingBoxDescent

    // Draw text
    ctx.fillText(textToRender, rect.x + theme.cellHorizontalPadding, textY)

    // Calculate text width
    const textWidth = textMetrics.width

    // Draw action icons on hover
    if (hoverAmount > 0 && textToRender.length > 0) {
      const iconX = rect.x + theme.cellHorizontalPadding + textWidth + 10

      const iconMetrics = ctx.measureText('M')
      const iconHeight = iconMetrics.actualBoundingBoxAscent + iconMetrics.actualBoundingBoxDescent
      const iconY = verticalCenter + iconHeight / 2 - iconMetrics.actualBoundingBoxDescent

      const icons: IconProperties[] = [
        {
          symbol: 'edit',
          xPosition: iconX,
          width: 24,
          action: 'edit',
          color: theme.textDark,
          preventActivation: false,
        },
        {
          symbol: 'delete',
          xPosition: iconX + 30,
          width: 24,
          action: 'delete',
          color: theme.textDark,
          preventActivation: true,
        },
      ]

      renderActionIcons(ctx, icons, iconY)
    }

    ctx.restore()
  },

  // Provide the editor component for editing the cell content
  provideEditor: () => {
    const style = {
      minWidth: '800px',
      overflow: 'hidden',
    }
    const customEditorHeight = getEditorStyle(565)

    return {
      disablePadding: true,
      styleOverride: { ...style, ...customEditorHeight },
      editor: RichTextEditor as ProvideEditorComponent<MarkdownCell>,
    }
  },

  /*
   * Handle cell click events
   * This includes editing and deleting cell content
   */
  onClick: (args: any): MarkdownCell | undefined => {
    const { cell, posX: clickX, bounds: rect } = args
    const markdownCell = cell.data.cell
    const dataEditorRef = cell.data.extra.dataEditorRef

    const { action, preventActivation } = handleCellClick(args)

    if (preventActivation) {
      args.preventDefault()
    }

    switch (action) {
      case 'edit':
        return undefined
      case 'delete':
        if (dataEditorRef && dataEditorRef.current) {
          setTimeout(() => {
            dataEditorRef.current.emit('delete')
          }, 0)
        } else {
          console.error('dataEditorRef is not available')
          toast.error('Unable to delete content')
        }
        break
      default:
        return undefined
    }

    return {
      ...args.cell,
      data: {
        ...args.cell.data,
        cell: markdownCell,
      },
    } as MarkdownCell
  },

  // Handle cell deletion
  onDelete: (args) => {
    const markdownCell = args.data.cell
    markdownCell.toCell('')
    toast.error('Markdown content deleted')
    return {
      ...args,
      data: {
        ...args.data,
        cell: markdownCell,
      },
    }
  },

  needsHover: true,
  needsHoverPosition: true,
}

export default CustomRichTextRenderer

/*
 * Function to render action icons
 * This is used to draw the edit and delete icons in the cell
 */
function renderActionIcons(ctx: CanvasRenderingContext2D, icons: IconProperties[], iconY: number) {
  ctx.font = ICON_FONT_STYLE
  icons.forEach((icon) => {
    ctx.fillStyle = icon.color
    ctx.fillText(icon.symbol, icon.xPosition, iconY)
  })
}

/*
 * Function to handle cell click
 * This determines which action to take based on where the cell was clicked
 */
const handleCellClick = (args: any): { action: string; preventActivation: boolean } => {
  const { cell, posX: clickX, bounds: rect, theme } = args
  const markdownText = cell.data.cell.toString()
  const isEmptyMarkdown = !markdownText || markdownText.trim() === ''

  if (isEmptyMarkdown) {
    return { action: 'edit', preventActivation: false }
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) return { action: '', preventActivation: false }

  ctx.font = theme.baseFontFull
  const textX = rect.x + theme.cellHorizontalPadding
  const textWidth = ctx.measureText(truncateMarkdownDisplay(markdownText)).width

  const clickArea = textX + textWidth

  if (rect.x + clickX <= clickArea) {
    return { action: 'edit', preventActivation: false }
  }

  const icons: IconProperties[] = [
    {
      symbol: 'edit',
      xPosition: textX + textWidth + 10,
      width: 24,
      action: 'edit',
      preventActivation: false,
      color: theme.textDark,
    },
    {
      symbol: 'delete',
      xPosition: textX + textWidth + 40,
      width: 24,
      action: 'delete',
      preventActivation: true,
      color: theme.textDark,
    },
  ]

  const rectClickX = rect.x + clickX
  for (const icon of icons) {
    if (rectClickX >= icon.xPosition && rectClickX < icon.xPosition + icon.width) {
      return { action: icon.action, preventActivation: icon.preventActivation }
    }
  }

  return { action: '', preventActivation: true }
}

/*
 * Function to strip markdown from text
 * This converts markdown to plain text by removing all markdown syntax
 */
export function stripMarkdown(markdown: string): string {
  return (
    removeMarkdown(markdown, {
      stripListLeaders: true,
      listUnicodeChar: '',
      gfm: true,
      useImgAltText: true,
    })
      // Remove HTML tags
      .replace(/<[^>]*>/g, '')
      // Remove Markdown headers
      .replace(/^#{1,6}\s+/gm, '')
      // Remove Markdown emphasis (bold, italic)
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      .replace(/(\*|_)(.*?)\1/g, '$2')
      // Remove Markdown links
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
      // Remove Markdown code blocks and inline code
      .replace(/`{3}[\s\S]*?`{3}/g, '')
      .replace(/`(.+?)`/g, '$1')
      // Remove Markdown list symbols
      .replace(/^[\*\-+]\s+/gm, '')
      // Remove Markdown blockquotes
      .replace(/^\>\s+/gm, '')
      // Remove Markdown horizontal rules
      .replace(/^(?:[-*_]){3,}\s*$/gm, '')
      // Replace multiple newlines with a single newline
      .replace(/\n{2,}/g, '\n')
      // Trim whitespace
      .trim()
  )
}
